import React, { useState } from 'react';
import { PATTERN_TYPES, DEFAULT_PATTERN_SETTINGS, copyDefaults, normaliseSettings } from './chartPatterns';

const fieldNames = {
  minBars:'Minimum candles', maxBars:'Maximum candles',
  tolerancePct:'Trendline / price tolerance (%)', contractionPct:'Minimum contraction (%)',
  widthVarPct:'Maximum channel-width change (%)', poleATR:'Minimum pole (ATR)',
  maxRetracePct:'Maximum pole retracement (%)', minSwingPct:'Minimum intervening swing (%)',
  headPct:'Minimum head projection (%)', minDepthPct:'Minimum cup depth (%)',
  maxDepthPct:'Maximum cup depth (%)', maxHandlePct:'Maximum handle retracement (%)',
  minHandleBars:'Minimum handle candles', maxHandleBars:'Maximum handle candles',
};
const panel = {position:'fixed',inset:0,zIndex:20000,background: 'var(--overlay)',display:'flex',justifyContent:'center',alignItems:'center',padding:'10px'};
const box = {background:'var(--surface)',color:'var(--text)',width:'min(760px,96vw)',maxHeight:'min(92dvh,920px)',overflowY:'auto',border:'1px solid var(--border)',borderRadius:'10px',padding:'20px',boxSizing:'border-box',fontSize:'13px'};
const row = {display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap'};
const field = {background:'var(--bg)',color:'var(--text)',border:'1px solid var(--border)',borderRadius:'4px',padding:'5px 7px'};
const button = {background:'var(--bg)',color:'var(--text)',border:'1px solid var(--border)',borderRadius:'5px',padding:'6px 10px',cursor:'pointer'};
const muted = {color:'var(--text-muted)',fontSize:'12px'};
const groups = ['Continuation','Triangles','Wedges & channels','Reversals'];

export default function ChartPatternSettings({settings,onChange,onClose}) {
  const [expanded,setExpanded]=useState(null);
  const update=partial=>onChange(normaliseSettings({...settings,...partial}));
  const setRule=(id,key,value)=>{
    const n=Number(value);
    if (!Number.isFinite(n)||n<0)return;
    update({rules:{...settings.rules,[id]:{...settings.rules[id],[key]:n}}});
  };
  const setShown=(id,val)=>update({visible:{...settings.visible,[id]:val}});
  return (
    <div style={panel} onMouseDown={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={box} role="dialog" aria-modal="true" aria-label="Automatic chart patterns">
        <div style={{...row,justifyContent:'space-between',marginBottom:'14px'}}>
          <strong style={{fontSize:'18px'}}>Automatic patterns</strong>
          <button style={button} onClick={onClose} aria-label="Close pattern settings">Close ✕</button>
        </div>
        <label style={{...row,fontWeight:600}}>
          <input type="checkbox" checked={settings.enabled} onChange={e=>update({enabled:e.target.checked})}/>
          Show automatic patterns on chart
        </label>
        <p style={muted}>Mathematical matches from the chart's existing daily candles. No AI calls. Automatic overlays are not saved with your manual drawings. Major pivots use the selected sensitivity; short patterns use one-third of it.</p>
        <p style={muted}>
          Shape references: <a href="https://chartschool.stockcharts.com/table-of-contents/chart-analysis/chart-patterns" target="_blank" rel="noopener noreferrer">ChartSchool</a>,
          {' '}<a href="https://business.columbia.edu/sites/default/files-efs/pubfiles/19268/Lo-Mamaysky_wang_foundations.pdf" target="_blank" rel="noopener noreferrer">Lo–Mamaysky–Wang</a> and
          {' '}<a href="https://www.fidelity.com/learning-center/trading-investing/technical-analysis/technical-indicator-guide/cup-with-handle" target="_blank" rel="noopener noreferrer">Fidelity</a>.
          Numerical tolerances without published standards are adjustable starting values, not validated predictive thresholds.
        </p>
        <div style={{...row,margin:'14px 0'}}>
          <label>Show
            <select style={{...field,marginLeft:8}} value={settings.stages} onChange={e=>update({stages:e.target.value})}>
              <option value="developing">Developing only (current)</option>
              <option value="both">Developing + confirmed</option>
              <option value="confirmed">Confirmed only</option>
              <option value="all">Include failed / expired</option>
            </select>
          </label>
          <label>Pivot sensitivity (%)
            <input style={{...field,width:65,marginLeft:6}} type="number" min="0.5" max="20" step="0.5" value={settings.pivotPct} onChange={e=>update({pivotPct:Number(e.target.value)})}/>
          </label>
          <label>ATR period
            <input style={{...field,width:58,marginLeft:6}} type="number" min="2" max="100" step="1" value={settings.atrPeriod} onChange={e=>update({atrPeriod:Number(e.target.value)})}/>
          </label>
          <label>Breakout buffer (ATR)
            <input style={{...field,width:65,marginLeft:6}} type="number" min="0" max="5" step="0.05" value={settings.breakoutAtr} onChange={e=>update({breakoutAtr:Number(e.target.value)})}/>
          </label>
        </div>
        <div style={{...row,marginBottom:14}}>
          <label style={row}><input type="checkbox" checked={settings.confirmVolume} onChange={e=>update({confirmVolume:e.target.checked})}/> Require breakout volume</label>
          <label>Min volume / 20-bar average
            <input style={{...field,width:65,marginLeft:6}} type="number" min="1" max="5" step="0.1" value={settings.volumeMultiplier} onChange={e=>update({volumeMultiplier:Number(e.target.value)})}/>
          </label>
        </div>
        <div style={{...row,marginBottom:10}}>
          <button style={button} onClick={()=>update({visible:Object.fromEntries(PATTERN_TYPES.map(p=>[p.id,true]))})}>Select all</button>
          <button style={button} onClick={()=>update({visible:Object.fromEntries(PATTERN_TYPES.map(p=>[p.id,false]))})}>Select none</button>
        </div>
        {groups.map(group=>(
          <div key={group} style={{borderTop:'1px solid var(--border)',padding:'12px 0'}}>
            <strong style={{display:'block',marginBottom:8}}>{group}</strong>
            {PATTERN_TYPES.filter(t=>t.group===group).map(t=>(
              <div key={t.id} style={{padding:'6px 0'}}>
                <div style={{...row,justifyContent:'space-between'}}>
                  <label style={row}>
                    <input type="checkbox" checked={settings.visible[t.id]} onChange={e=>setShown(t.id,e.target.checked)}/>
                    {t.name}
                  </label>
                  <button style={button} onClick={()=>setExpanded(expanded===t.id?null:t.id)}
                    aria-expanded={expanded===t.id}>{expanded===t.id?'Hide settings':'Adjust defaults ▾'}</button>
                </div>
                {expanded===t.id&&(
                  <div style={{background:'var(--bg)',borderRadius:'6px',padding:12,marginTop:6}}>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(185px,1fr))',gap:'10px'}}>
                      {Object.keys(settings.rules[t.id]).map(key=>(
                        <label key={key} style={{display:'flex',flexDirection:'column',gap:5}}>
                          <span style={muted}>{fieldNames[key]}</span>
                          <input style={field} type="number" min="0" max="500" step={key==='poleATR'||key==='tolerancePct'?'0.1':'1'}
                            value={settings.rules[t.id][key]} onChange={e=>setRule(t.id,key,e.target.value)}/>
                        </label>
                      ))}
                    </div>
                    <div style={{...row,justifyContent:'space-between',marginTop:10}}>
                      <span style={muted}>Preferences sync to your account, with a browser copy.</span>
                      <button style={button} onClick={()=>update({rules:{...settings.rules,[t.id]:{...DEFAULT_PATTERN_SETTINGS.rules[t.id]}}})}>Reset {t.name}</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
        <div style={{...row,justifyContent:'space-between',marginTop:12}}>
          <button style={button} onClick={()=>onChange(copyDefaults())}>Reset all to defaults</button>
          <button style={{...button,background:'var(--accent)',color:'var(--on-accent)',borderColor:'var(--accent)'}} onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
